import React from "react";
import { CreateFunction, UpdateFunction } from "@hex-insights/app-modules";
import {
	Button,
	ClassNameProps,
	dateDiff,
	DistributiveOmit,
	Modal,
	Row,
	StyleProps,
	toGrammaticalNumber,
} from "@hex-insights/core";
import { Form, FormEditButtonPair, FormState, settings, SubmitButton, useInternalField } from "@hex-insights/forms";
import {
	NewsletterCreateMutation,
	NewsletterDetailQuery,
	NewsletterUpdateMutation,
	useInternalPortalsViewerInfoQuery,
} from "../../../Utilities";
import { AuthorTimestampView, ModalNewsletter, NewsletterModal } from "../../Components/Newsletter";
import { NewsletterFormState, NewsletterFormValues } from "../../FormUtilities";
import { UserUtilities } from "../../Utilities";
import { BaseFormProps } from "../shared";
import * as Field from "./Field";
import {
	useCreateFormControl,
	useCreateFormSubmission,
	useDetailFormControl,
	useDetailFormSubmission,
	useReadOnlyForm,
} from "./hooks";
import styles from "./form.module.css";

export {
	type UseCreateFormArgs,
	useCreateForm,
	type UseDetailFormArgs,
	useDetailForm,
	type UseReadOnlyFormArgs,
	useReadOnlyForm,
} from "./hooks";

export type ControlledCreateProps = Omit<
	CreateProps<NewsletterCreateMutation["createNewsletter"]>,
	"formState" | "create"
> & {
	formStateArgs?: NewsletterFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the Newsletter model using an internally managed form state.
 */
export function ControlledCreate({ formStateArgs, ...props }: ControlledCreateProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <Create formState={formState} create={create} {...props} />;
}

export type CreateProps<T> = {
	formState: FormState<NewsletterFormValues.Create>;
	create: CreateFunction<NewsletterFormValues.Create, T>;
	onSuccess: (formState: FormState<NewsletterFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
	columnClassName?: string;
	columnStyle?: React.CSSProperties;
} & BaseFormProps<NewsletterFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the Newsletter model using the given form state.
 */
export function Create<T>({
	formState,
	create,
	onSuccess,
	formNameSuffix,
	columnClassName,
	columnStyle,
	...props
}: CreateProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "newsletter.create" + (formNameSuffix ? "." + formNameSuffix : "");

	const { data } = useInternalPortalsViewerInfoQuery();
	const viewer = data?.viewer;
	const { formValues } = formState;
	const previewNewsletter = React.useMemo<ModalNewsletter>(
		() => ({
			...formValues,
			id: "preview",
			author: viewer ?? UserUtilities.placeholderCurrentUser,
			imageURL: formValues.imageKey
				? settings.uploads.fileInfoToURL(formValues.imageKey, formValues.imageContentType)
				: "",
		}),
		[viewer, formValues],
	);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const { releaseAt } = formState.formValues;
	let submitButtonText = "Save Newsletter";
	if (releaseAt === null) {
		submitButtonText = "Save Newsletter as Draft";
	} else if (dateDiff(new Date(releaseAt), new Date(), "hour") < 1) {
		submitButtonText = "Publish Newsletter";
	} else {
		submitButtonText = "Schedule Newsletter";
	}

	return (
		<React.Fragment>
			<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
				<Form.Column className={columnClassName} style={columnStyle}>
					<CreateFields formState={formState} />

					<ReleaseTimingNotice releaseAt={releaseAt} />

					<Row justify="spaced-end">
						<Button variant="secondary" onClick={openModal}>
							Preview
						</Button>
						<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
							{submitButtonText}
						</SubmitButton>
					</Row>
				</Form.Column>
			</Form>

			<Modal.If condition={isModalOpen}>
				<NewsletterModal
					newsletter={previewNewsletter}
					noReactions
					noComments
					noOtherNewsletters
					isPreview
					withBackButton
					backButtonText="Back to Form"
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

type CreateFieldsProps = {
	formState: FormState<NewsletterFormValues.Create>;
};

function CreateFields({ formState }: CreateFieldsProps) {
	const { data } = useInternalPortalsViewerInfoQuery();
	const viewer = data?.viewer;

	const {
		formValues: { releaseAt },
	} = formState;

	const previewNewsletter = React.useMemo(
		() => ({
			releaseAt,
			author: viewer ?? UserUtilities.placeholderCurrentUser,
		}),
		[releaseAt, viewer],
	);

	return (
		<React.Fragment>
			<Field.Title formState={formState} style={{ width: "100%", marginBottom: "1rem" }} />
			<AuthorTimestampView newsletter={previewNewsletter} />
			<Field.Image formState={formState} style={{ width: "100%" }} />
			<Field.Body formState={formState} noHeader style={{ width: "100%" }} />
			<Field.SharingPolicy.SubForm formState={formState} />
			<Field.ReleaseAt formState={formState} />
		</React.Fragment>
	);
}

type ReleaseTimingNoticeProps = {
	releaseAt: NewsletterFormValues.Base["releaseAt"];
};

function ReleaseTimingNotice({ releaseAt }: ReleaseTimingNoticeProps) {
	if (releaseAt === null) {
		return (
			<div className={styles["release-timing-notice"]}>
				This newsletter will be <span className={styles["release-timing-notice__primary-value"]}>saved as a draft</span>{" "}
				and will not be visible to others until a release time is set.
			</div>
		);
	}

	const releaseAtDate = new Date(releaseAt);
	const isReleaseAtThisYear = releaseAtDate.getFullYear() === new Date().getFullYear();
	const timeToRelease = Math.round(dateDiff(releaseAtDate, new Date(), "hours", false));
	const timeToReleaseDays = Math.floor(timeToRelease / 24);
	const timeToReleaseHours = timeToRelease % 24;
	let timeToReleaseText = "";
	if (timeToReleaseDays === 0 && timeToReleaseHours === 0) {
		timeToReleaseText = "now";
	} else if (timeToReleaseDays === 0) {
		timeToReleaseText = "in " + toGrammaticalNumber("hour", timeToReleaseHours, true);
	} else if (timeToReleaseHours === 0) {
		timeToReleaseText = "in " + toGrammaticalNumber("day", timeToReleaseDays, true);
	} else {
		timeToReleaseText = `in ${toGrammaticalNumber("day", timeToReleaseDays, true)} and ${toGrammaticalNumber(
			"hour",
			timeToReleaseHours,
			true,
		)}`;
	}

	return (
		<div className={styles["release-timing-notice"]}>
			This newsletter will be released{" "}
			<span className={styles["release-timing-notice__primary-value"]}>{timeToReleaseText}</span> on{" "}
			<span className={styles["release-timing-notice__primary-value"]}>
				{releaseAtDate.toLocaleString(undefined, {
					year: isReleaseAtThisYear ? undefined : "numeric",
					month: "short",
					day: "numeric",
					weekday: "short",
					hour: "numeric",
					minute: "2-digit",
				})}
			</span>
			.
		</div>
	);
}

export type ControlledDetailProps = Omit<
	DetailProps<NewsletterUpdateMutation["updateNewsletter"]>,
	"formState" | "update"
> & {
	formStateArgs?: Partial<DistributiveOmit<NewsletterFormState.UseDetailFormStateArgs, "initialFormValues">>;
};

/**
 * Detail form of the Newsletter model using an internally managed form state.
 */
export function ControlledDetail({ formStateArgs, ...props }: ControlledDetailProps) {
	const { formState, update } = useDetailFormControl({ ...formStateArgs, newsletter: props.newsletter });

	return <Detail formState={formState} update={update} {...props} />;
}

export type DetailProps<T> = {
	newsletter: NewsletterDetailQuery["newsletter"];
	formState: FormState<NewsletterFormValues.Detail>;
	update: UpdateFunction<NewsletterFormValues.Detail, T>;
	onSuccess: (formState: FormState<NewsletterFormValues.Detail>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<NewsletterFormValues.Detail> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Detail form of the Newsletter model using the given form state.
 */
export function Detail<T>({ formState, newsletter, update, onSuccess, formNameSuffix, ...props }: DetailProps<T>) {
	const { onSubmit } = useDetailFormSubmission({ formState, update, onSuccess });

	const formName = `newsletter.detail.${newsletter.id}` + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column style={{ width: "100%" }}>
				<FormEditButtonPair formState={formState} />

				<DetailFields formState={formState} newsletter={newsletter} />
			</Form.Column>
		</Form>
	);
}

export type ControlledReadOnlyProps = Omit<ReadOnlyProps, "formState"> & {
	formStateArgs?: Partial<DistributiveOmit<NewsletterFormState.UseReadOnlyFormStateArgs, "initialFormValues">>;
};

/**
 * Read-only detail form of the Newsletter model using an internally managed form state.
 */
export function ControlledReadOnly({ formStateArgs, ...props }: ControlledReadOnlyProps) {
	const { formState } = useReadOnlyForm({ ...formStateArgs, newsletter: props.newsletter });

	return <ReadOnly formState={formState} {...props} />;
}

export type ReadOnlyProps = {
	newsletter: NewsletterDetailQuery["newsletter"];
	formState: FormState<NewsletterFormValues.Detail>;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Read-only detail form of the Newsletter model using the given form state.
 */
export function ReadOnly({ formState, newsletter, className, style }: ReadOnlyProps) {
	return (
		<div className={className} style={style}>
			<Form.Column>
				<DetailFields formState={formState} newsletter={newsletter} />
			</Form.Column>
		</div>
	);
}

type DetailFieldsProps = {
	newsletter: NewsletterDetailQuery["newsletter"];
	formState: FormState<NewsletterFormValues.Detail>;
};

function DetailFields({ formState, newsletter }: DetailFieldsProps) {
	useInternalField(formState, "authorID");

	return (
		<React.Fragment>
			<Field.Title formState={formState} style={{ width: "100%", marginBottom: "1rem" }} />
			<AuthorTimestampView newsletter={newsletter} />
			<Field.Image formState={formState} url={newsletter.imageURL} style={{ width: "100%" }} />
			<Field.Body formState={formState} noHeader style={{ width: "100%" }} />
			<Field.SharingPolicy.Detail sharingPolicy={newsletter.sharingPolicy} />
			<Field.ReleaseAt formState={formState} />
		</React.Fragment>
	);
}
